import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Loader } from '@ophelos/ui/components/loader';
import { ScrollArea } from '@ophelos/ui/components/scroll-area';
import { Check, Save, Undo2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useScrollDetection } from '../../../hooks/useScrollDetection';
import { ScopePreviewButton } from './ScopePreviewButton';

export type SaveStatus = 'idle' | 'saving' | 'success' | 'error';

interface SettingsPageShellProps {
  title: string;
  description: string;
  children: ReactNode;
  /** Omit the save bar entirely for read-only pages. */
  hasChanges?: boolean;
  /** False to disable the Save button while the form has validation errors that block
   *  saving. Defaults to true; the bar still only renders when `hasChanges` is true. */
  canSave?: boolean;
  onSave?: () => void;
  onReset?: () => void;
  saveStatus?: SaveStatus;
  isLoading?: boolean;
  loadingText?: string;
  /** Actions rendered left of the save bar (e.g. a "New destination" button). */
  actions?: ReactNode;
  maxWidth?: 'max-w-4xl' | 'max-w-5xl' | 'max-w-6xl';
  /** False for pages that do not edit a configuration scope, so the hierarchy preview button is not offered. */
  scopePreview?: boolean;
  testId?: string;
}

export function SettingsPageShell({
  title,
  description,
  children,
  hasChanges = false,
  canSave = true,
  onSave,
  onReset,
  saveStatus = 'idle',
  isLoading = false,
  loadingText = 'Loading configuration...',
  actions,
  maxWidth = 'max-w-5xl',
  scopePreview = true,
  testId,
}: SettingsPageShellProps) {
  const { scrollAreaRef, isScrolled } = useScrollDetection();
  const savable = Boolean(onSave);

  return (
    <div className="flex flex-col h-(--page-main-height) overflow-hidden">
      <div className="bg-background h-full stack flex-1 stack">
        <div
          className={`shrink-0 flex items-center transition-all duration-200 border-b pb-2 ${isScrolled ? 'mt-2 border-border-light' : 'mt-8 border-transparent'}`}
        >
          <div className={`${maxWidth} mx-auto px-6 w-full flex justify-between items-center`}>
            <div className="flex gap-2 w-full">
              <div className="stack">
                <div className="flex gap-4">
                  <h1 className="text-2xl font-semibold tracking-tight font-display">{title}</h1>
                  <div className="flex items-center gap-2">
                    {savable && hasChanges && (
                      <Badge variant="warning" className="text-xs">
                        Unsaved changes
                      </Badge>
                    )}
                    {savable && saveStatus === 'success' && (
                      <Badge variant="success" className="text-xs">
                        <Check className="size-3 mr-1" />
                        Saved
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {scopePreview && <ScopePreviewButton />}
              {actions}
              {savable && hasChanges && (
                <>
                  {onReset && (
                    <Button variant="outline" onClick={onReset}>
                      <Undo2 className="size-4" />
                      Undo Changes
                    </Button>
                  )}
                  <Button variant="default" onClick={onSave} disabled={saveStatus === 'saving' || !canSave}>
                    <Save className="size-4" />
                    {saveStatus === 'saving' ? 'Saving...' : 'Save'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
        <ScrollArea ref={scrollAreaRef} className="flex-1 min-h-0">
          {isLoading && <Loader text={loadingText} colour="default" className="mt-40" />}
          {/* Kept mounted while loading: the form owns the fetch that clears `isLoading`,
              so unmounting it here would leave the page loading forever. */}
          <div className={`mt-6 ${maxWidth} mx-auto px-6 pb-10 stack w-full ${isLoading ? 'hidden' : ''}`} data-testid={testId}>
            {children}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
